import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  Signal,
  OnChanges,
  OnInit,
  OnDestroy,
  SimpleChanges,
  TemplateRef,
  computed,
  inject,
} from '@angular/core';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faUtensils,
  faTags,
  faHome,
  faReceipt,
  faBookOpen,
  faCalendarDays,
  faFileExport,
  faCartShopping,
  faUser,
  faArrowLeft,
  faUserFriends,
  faShieldHalved,
  faEllipsisH,
  faClipboardCheck,
} from '@fortawesome/free-solid-svg-icons';
import { Subject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { UserWithDefaults } from '../../models/user';
import { PageHeaderService, PageHeaderBackButton, PageHeaderTitleBadge } from '../../services/page-header/page-header.service';
import { ThemeService } from '../../services/theme.service';
import { SmartBackService } from '../../services/navigation/smart-back.service';
import { InvitationsComponent } from '../invitations/invitations.component';
import { AppLogger, AppLoggerService } from '../../core/logging';
import { InvitationResponse } from '../../models/invitationResponse';
import { InvitationService } from '../../services/invitation/invitation.service';
import { AppEventsService } from '../../services/app-events/app-events.service';
import { UiToastService, UiButtonComponent } from '../../shared/ui';
import { ConsumptionCheckStore } from '../../services/consumption/consumption-check-store.service';
import { ApiinfoService } from '../../services/meta/apiinfo.service';
import { ApiInfo } from '../../models/apiinfo';
import { environment } from '../../env';
import {
  PageAction,
  PageActionButton,
  PageActionCustom,
  PageActionTone,
  isButtonAction,
  isCustomAction,
} from './page-action.model';

/**
 * AppShellComponent — successor of AppNavComponent.
 *
 * Same visual layout (drawer + top header + sub-header toolbar) but with a
 * cleaner, declarative action API:
 *
 *  - Pages declare actions as a `PageAction[]` array via
 *    `PageHeaderService.setPageActions(...)`.
 *  - Buttons auto-collapse into a `⋯` overflow dropdown on mobile, unless
 *    `pinned: true`.
 *  - Custom (template-based) actions stay in the toolbar by default; pages can
 *    opt them into the mobile overflow with `moveToSubMenuOnMobile: true`.
 *
 * Legacy template-based APIs on PageHeaderService (setActions / setMenuActions
 * / setPinnedActions / setSearch) continue to work in parallel, so pages can
 * be migrated incrementally.
 */
@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FontAwesomeModule,
    InvitationsComponent,
    UiButtonComponent,
  ],
  templateUrl: './app-shell.component.html',
  styleUrl: './app-shell.component.scss',
})
export class AppShellComponent implements OnChanges, OnInit, OnDestroy {
  private readonly log: AppLogger;
  private readonly destroy$ = new Subject<void>();

  @Input({ required: true }) user: UserWithDefaults | null = null;

  /** Callback invoked when the user clicks logout. */
  @Input() onLogout: (() => void) | null = null;

  // --- Page header (legacy template-based API, still supported) -------------
  pageActionsTemplate: Signal<TemplateRef<unknown> | null>;
  pagePinnedActionsTemplate: Signal<TemplateRef<unknown> | null>;
  pageMenuActionsTemplate: Signal<TemplateRef<unknown> | null>;
  pageSearch: Signal<TemplateRef<unknown> | null>;
  backButton: Signal<PageHeaderBackButton | null>;
  pageTitle: Signal<string | null>;
  titleBadge: Signal<PageHeaderTitleBadge | null>;

  // --- Page header (new declarative API) ------------------------------------
  pageActions: Signal<PageAction[] | null>;

  /** Buttons rendered inline on desktop. Excludes always-in-submenu items. */
  desktopButtons = computed<PageActionButton[]>(() =>
    (this.pageActions() ?? [])
      .filter(isButtonAction)
      .filter((a) => !a.alwaysInSubMenu && this.isVisibleOn(a, 'desktop'))
  );

  /** Buttons pinned to the mobile toolbar (always shown on mobile). */
  mobileToolbarButtons = computed<PageActionButton[]>(() =>
    (this.pageActions() ?? [])
      .filter(isButtonAction)
      .filter(
        (a) =>
          !a.alwaysInSubMenu &&
          this.isVisibleOn(a, 'mobile') &&
          this.isPinned(a)
      )
  );

  /** Buttons collapsed into the mobile overflow dropdown. */
  mobileOverflowButtons = computed<PageActionButton[]>(() =>
    (this.pageActions() ?? [])
      .filter(isButtonAction)
      .filter(
        (a) =>
          !a.alwaysInSubMenu &&
          this.isVisibleOn(a, 'mobile') &&
          !this.isPinned(a)
      )
  );

  /**
   * Buttons forced into the overflow dropdown on every viewport
   * (mobile + desktop) via `alwaysInSubMenu: true`.
   */
  alwaysSubMenuButtons = computed<PageActionButton[]>(() =>
    (this.pageActions() ?? [])
      .filter(isButtonAction)
      .filter((a) => !!a.alwaysInSubMenu)
  );

  /** Custom items in toolbar on desktop. Excludes always-in-submenu items. */
  desktopCustom = computed<PageActionCustom[]>(() =>
    (this.pageActions() ?? [])
      .filter(isCustomAction)
      .filter((a) => !a.alwaysInSubMenu && this.isVisibleOn(a, 'desktop'))
  );

  /** Custom items in toolbar on mobile (default behaviour: not pinned, not moved to submenu). */
  mobileCustomToolbar = computed<PageActionCustom[]>(() =>
    (this.pageActions() ?? [])
      .filter(isCustomAction)
      .filter(
        (a) =>
          !a.alwaysInSubMenu &&
          this.isVisibleOn(a, 'mobile') &&
          !a.moveToSubMenuOnMobile &&
          !a.pinned
      )
  );

  /**
   * Custom items that should render in the full-width second row on mobile
   * (via `pinned: true`). On desktop they remain inline in the toolbar.
   */
  mobilePinnedCustom = computed<PageActionCustom[]>(() =>
    (this.pageActions() ?? [])
      .filter(isCustomAction)
      .filter(
        (a) =>
          !a.alwaysInSubMenu &&
          this.isVisibleOn(a, 'mobile') &&
          !!a.pinned
      )
  );

  /** Custom items moved into the mobile overflow dropdown. */
  mobileCustomOverflow = computed<PageActionCustom[]>(() =>
    (this.pageActions() ?? [])
      .filter(isCustomAction)
      .filter(
        (a) =>
          !a.alwaysInSubMenu &&
          this.isVisibleOn(a, 'mobile') &&
          !!a.moveToSubMenuOnMobile
      )
  );

  /**
   * Custom items forced into the overflow dropdown on every viewport via
   * `alwaysInSubMenu: true`.
   */
  alwaysSubMenuCustom = computed<PageActionCustom[]>(() =>
    (this.pageActions() ?? [])
      .filter(isCustomAction)
      .filter((a) => !!a.alwaysInSubMenu)
  );

  /** True if any overflow content exists on mobile (buttons or custom). */
  hasMobileOverflow = computed<boolean>(
    () =>
      this.mobileOverflowButtons().length > 0 ||
      this.mobileCustomOverflow().length > 0 ||
      !!this.pageMenuActionsTemplate()
  );

  /**
   * True if the overflow dropdown should be visible on desktop. This is the
   * case when legacy menu actions are set OR when any action opts into
   * `alwaysInSubMenu`.
   */
  hasDesktopOverflow = computed<boolean>(
    () =>
      this.alwaysSubMenuButtons().length > 0 ||
      this.alwaysSubMenuCustom().length > 0 ||
      !!this.pageMenuActionsTemplate()
  );

  /** True if any declarative or legacy action exists in the toolbar area. */
  hasAnyToolbarContent = computed<boolean>(
    () =>
      (this.pageActions()?.length ?? 0) > 0 ||
      !!this.pageActionsTemplate() ||
      !!this.pagePinnedActionsTemplate() ||
      !!this.pageMenuActionsTemplate()
  );

  private routerSubscription?: Subscription;
  private lastRoutePath = '';

  /** Current URL path without query params. */
  currentPath = '/';

  // Icons (self-contained template).
  faUtensils = faUtensils;
  faAssortment = faTags;
  faHome = faHome;
  faHousehold = faHome;
  faRecipes = faBookOpen;
  faMealPlanner = faCalendarDays;

  /** Meal planner nav entry is behind an environment flag (off in prod for now). */
  readonly mealPlannerEnabled = environment.mealPlanner.enabled;
  faShopping = faCartShopping;
  faReceipt = faReceipt;
  faExport = faFileExport;
  faUser = faUser;
  faArrowLeft = faArrowLeft;
  faInvitationIndicator = faUserFriends;
  faAdmin = faShieldHalved;
  faEllipsisH = faEllipsisH;
  faQuickCheck = faClipboardCheck;

  /**
   * Open consumption reminders. The nav entry only exists while there is
   * something to answer — a permanent "Quick Check" item that is empty most of
   * the time reads as broken.
   */
  readonly openQuickChecks = inject(ConsumptionCheckStore).openCount;

  invites: InvitationResponse[] = [];
  mobileInvitationsExpanded = false;

  apiInfo: ApiInfo | null = null;
  backendVersion: string | null = null;

  themeMode: 'system' | 'light' | 'dark' = 'system';
  darkMode = false;

  private readonly router = inject(Router);
  private readonly pageHeaderService = inject(PageHeaderService);
  private readonly themeService = inject(ThemeService);
  private readonly smartBack = inject(SmartBackService);
  private readonly appLogger = inject(AppLoggerService);
  private readonly invitationService = inject(InvitationService);
  private readonly appEvents = inject(AppEventsService);
  private readonly toastService = inject(UiToastService);
  private readonly apiinfoService = inject(ApiinfoService);
  private readonly hostRef = inject(ElementRef<HTMLElement>);

  /**
   * Close the overflow `<details>` dropdown when the user taps anywhere
   * outside of it. Native `<details>` doesn't do this on its own, but it's
   * the expected behaviour for a popover-style menu — and avoids the iOS
   * Safari "stuck open" issue we'd see with the focus-based DaisyUI dropdown.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const root = this.hostRef.nativeElement as HTMLElement;
    const detailsList = root.querySelectorAll<HTMLDetailsElement>(
      'details.dropdown[open]'
    );
    if (!detailsList.length) return;

    const target = event.target as Node | null;
    detailsList.forEach((details) => {
      if (target && !details.contains(target)) {
        details.removeAttribute('open');
      }
    });
  }

  constructor() {
    this.log = this.appLogger.createForClass(AppShellComponent);

    this.pageActionsTemplate = this.pageHeaderService.actions;
    this.pagePinnedActionsTemplate = this.pageHeaderService.pinnedActions;
    this.pageMenuActionsTemplate = this.pageHeaderService.menuActions;
    this.pageSearch = this.pageHeaderService.search;
    this.backButton = this.pageHeaderService.backButton;
    this.pageTitle = this.pageHeaderService.pageTitle;
    this.titleBadge = this.pageHeaderService.titleBadge;
    this.pageActions = this.pageHeaderService.pageActions;
  }

  // ----- lifecycle ---------------------------------------------------------

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['user']) return;

    if (this.user) {
      this.loadInvitations();
      return;
    }
    this.invites = [];
    this.mobileInvitationsExpanded = false;
  }

  ngOnInit(): void {
    this.themeMode = this.themeService.getMode();
    this.darkMode = this.themeService.getTheme() === 'dark';

    this.lastRoutePath = this.router.url.split('?')[0];
    this.currentPath = this.lastRoutePath;

    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const currentPath = event.urlAfterRedirects.split('?')[0];
        this.currentPath = currentPath;

        if (currentPath !== this.lastRoutePath) {
          this.mobileInvitationsExpanded = false;
          this.pageHeaderService.clearAll();
          this.lastRoutePath = currentPath;
        }
      });

    this.appEvents.invitationsChanged$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.loadInvitations());

    this.apiinfoService
      .getApiInfo()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (info) => {
          this.apiInfo = info ?? null;
          this.backendVersion = info?.version ?? null;
        },
        error: (err) => {
          this.log.debug('Failed to load API info', { err });
          this.apiInfo = null;
          this.backendVersion = null;
        },
      });
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ----- nav helpers -------------------------------------------------------

  isPathActive(prefix: string): boolean {
    if (!prefix.startsWith('/')) prefix = `/${prefix}`;
    const path = this.currentPath || '/';
    return path === prefix || path.startsWith(prefix + '/');
  }

  logout(): void {
    this.onLogout?.();
  }

  closeDrawerOnMobile(drawerToggle: HTMLInputElement): void {
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 1023.98px)').matches) {
      drawerToggle.checked = false;
    }
  }

  onThemeModeChange(mode: 'system' | 'light' | 'dark'): void {
    this.themeMode = mode;
    this.themeService.setMode(mode);
    this.darkMode = this.themeService.getTheme() === 'dark';
  }

  toggleMobileInvitations(): void {
    if (!this.invites.length) return;
    this.mobileInvitationsExpanded = !this.mobileInvitationsExpanded;
  }

  closeMobileInvitations(): void {
    this.mobileInvitationsExpanded = false;
  }

  acceptInvitation(invitationId: number): void {
    this.invitationService.acceptInvitation(invitationId).subscribe(() => {
      this.log.info('Invitation accepted', { invitationId });
      this.loadInvitations();
    });
  }

  rejectInvitation(invitationId: number): void {
    this.invitationService.rejectInvitation(invitationId).subscribe(() => {
      this.toastService.success('Invitation rejected', { durationMs: 3000 });
      this.loadInvitations();
    });
  }

  onBackClick(): void {
    const cfg = this.backButton();
    if (!cfg) return;

    if (cfg.onClick) {
      cfg.onClick();
      return;
    }

    if (cfg.preferHistoryBack) {
      const fallbackUrl =
        typeof cfg.fallbackUrl === 'string' && cfg.fallbackUrl.length
          ? cfg.fallbackUrl
          : '/products';
      this.smartBack.back({ fallbackUrl });
      return;
    }

    if (cfg.link) {
      if (Array.isArray(cfg.link)) {
        void this.router.navigate(cfg.link);
      } else {
        void this.router.navigateByUrl(cfg.link);
      }
      return;
    }

    void this.router.navigateByUrl('/products');
  }

  closeDropdown(detailsEl?: HTMLDetailsElement | null): void {
    if (!detailsEl) return;
    detailsEl.removeAttribute('open');
  }

  // ----- action helpers ----------------------------------------------------

  /**
   * Whether a button should always be visible (pinned). Both the convenience
   * `pinned` flag and `visibility: 'always'` opt in.
   */
  private isPinned(a: PageActionButton): boolean {
    return a.pinned === true || a.visibility === 'always';
  }

  /**
   * Visibility filter per viewport. Honors `visibility` overrides.
   */
  private isVisibleOn(a: PageAction, viewport: 'desktop' | 'mobile'): boolean {
    const v = (a as PageActionButton).visibility;
    if (v === 'desktop-only') return viewport === 'desktop';
    if (v === 'mobile-only') return viewport === 'mobile';
    return true;
  }

  /** Resolve the UiButton `tone` for a given action button. */
  toneFor(a: PageActionButton): PageActionTone {
    if (a.tone) return a.tone;
    if (a.variant === 'primary' || a.variant === 'secondary') return 'solid';
    return 'soft';
  }

  /** Resolve the UiButton `variant` for a given action button. */
  variantFor(a: PageActionButton) {
    return a.variant ?? 'secondary';
  }

  /** Menu item class for the mobile overflow dropdown (danger → red). */
  overflowItemClass(a: PageActionButton): string {
    return a.variant === 'danger' ? 'text-error' : '';
  }

  /** Invoke an action's onClick (with safety for disabled/loading). */
  runAction(a: PageActionButton, event: Event, closeMenu?: HTMLElement | null): void {
    if (a.disabled || a.loading) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    a.onClick(event);

    // Close DaisyUI dropdown after a menu item is invoked.
    if (closeMenu) {
      const details = closeMenu.closest('details') as HTMLDetailsElement | null;
      details?.removeAttribute('open');
      // Also blur for `tabindex` dropdowns.
      (document.activeElement as HTMLElement | null)?.blur?.();
    }
  }

  trackAction = (_: number, a: PageAction) => a.id ?? a;

  private loadInvitations(): void {
    if (!this.user) {
      this.invites = [];
      this.mobileInvitationsExpanded = false;
      return;
    }

    this.invitationService.getAllInvitations().subscribe((data) => {
      this.invites = data ?? [];
      if (this.invites.length === 0) {
        this.mobileInvitationsExpanded = false;
      }
    });
  }
}






